/**
 * Client ของคิวข้อขัดแย้ง Offline Sync — พูดกับสองเส้นทางของ
 * `docs/API_CONTRACT.md` (D-077) และ **ไม่พูดกับเส้นทางอื่นเลย**
 *
 * หลักการเดียวกับ `lib/lotTraceability/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาว่า "สำเร็จแล้ว" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "คิวว่าง" — คิวว่างกับอ่านไม่ได้
 *   ต้องแยกกันให้ขาด หน้าจอที่บอกว่าไม่มีอะไรค้างทั้งที่อ่านคำตอบไม่ออก คือคำสั่ง
 *   หน้างานที่ถูกปฏิเสธแล้วไม่มีใครเห็น ซึ่งเป็นสิ่งที่กฎ Sync ข้อ 4 ห้ามไว้ตรง ๆ
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *
 * **สิ่งที่ไฟล์นี้ไม่มีโดยเจตนา: เส้นทางที่ทำให้คำสั่งลงจริง** — ไม่มี retry ไม่มี
 * force ไม่มีการเรียก scan/staging service ใด ๆ `resolveOfflineSyncConflict()` ส่ง
 * ได้เพียง `{ outcome }` เพราะ contract ไม่มีทางให้ขอสิ่งนั้น (business rule 5,
 * D-077) การเพิ่มฟังก์ชันที่ post คำสั่งจากที่นี่ คือการเปิดทางให้สต๊อก/บัญชีถูก
 * บังคับให้ลงทั้งที่ invariant ไม่ผ่าน
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import {
  type ConflictOutcome,
  type OfflineSyncConflict,
  type OfflineSyncConflictPage,
  type OfflineSyncConflictQuery,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const OFFLINE_SYNC_CONFLICTS_PATH = '/v1/offline-sync/conflicts';

/** `POST /v1/offline-sync/conflicts/{conflict_id}/resolve` — บันทึกการตัดสินหนึ่งครั้ง */
export function conflictResolvePath(conflictId: string): string {
  return `${OFFLINE_SYNC_CONFLICTS_PATH}/${encodeURIComponent(conflictId)}/resolve`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — token ที่บอกว่าเป็นการปฏิเสธข้อไหน (อ่านด้วย `ruleFromFailure`) */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ **สำหรับกลุ่มนี้เท่านั้น**
 *
 * ไม่มี `rule-violation` (`422`) ในชุดนี้โดยเจตนา: `router.py` ประกาศไว้ชัดว่า
 * ทั้งสองเส้น **ไม่มี `422`** เพราะกลุ่มนี้ไม่บังคับกฎธุรกิจเรื่องสต๊อก/ใบสั่ง/บัญชี
 * เลย — มันไม่ post อะไร สถานะที่ประกาศไว้เกินจริงคือข้อความที่ไม่มีวันแสดง และ
 * เป็นกิ่งที่ไม่มีใครเคยเห็นว่าถูกหรือผิด
 *
 * `not-found` กับ `conflict` เกิดได้เฉพาะกับการ resolve: การ list ที่ไม่เจออะไรคือ
 * `items: []` ซึ่งเป็นข้อเท็จจริง ไม่ใช่การปฏิเสธ
 *
 * ⚠️ `not-found` ของกลุ่มนี้กลืนสองความหมายเข้าด้วยกัน **โดยตั้งใจฝั่งเซิร์ฟเวอร์**:
 * conflict ที่ไม่มีอยู่จริง กับ conflict ที่มีอยู่แต่อุปกรณ์ต้นทางอยู่นอก plant scope
 * ของผู้เรียก (D-004) — การแยกสองกรณีเท่ากับยืนยันว่ามี conflict อยู่ในโรงงานที่
 * ผู้เรียกมองไม่เห็น
 */
export type OfflineConflictFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type OfflineConflictListResult =
  | { readonly status: 'ready'; readonly page: OfflineSyncConflictPage }
  | OfflineConflictFailure;

export type OfflineConflictResolveResult =
  | { readonly status: 'ready'; readonly conflict: OfflineSyncConflict }
  | OfflineConflictFailure;

export interface OfflineConflictRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/** rule token ของการปฏิเสธ (ถ้ามี) — `CONFLICT_ALREADY_RESOLVED` อ่านจากที่นี่ */
export function ruleFromFailure(failure: OfflineConflictFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/* --------------------------------------------------------------- query ---- */

/**
 * ต่อ query string ของการ list
 *
 * `resolved` ถูกส่งไป **เฉพาะตอนที่เป็น `true`** เพราะ contract ระบุว่าค่าเริ่มต้น
 * ฝั่งเซิร์ฟเวอร์คือ open เท่านั้น — มุมมองปกติจึงยิงไปที่เส้นทางเปล่า ๆ ตรงตามที่
 * เอกสารเขียนไว้ ไม่ใช่เส้นทางที่พ่วงค่าซึ่งแปลว่าเหมือนกันแต่เขียนต่างออกไป
 */
export function buildConflictListQuery(query: OfflineSyncConflictQuery = {}): string {
  const params = new URLSearchParams();
  if (query.resolved === true) {
    params.append('resolved', 'true');
  }
  if (typeof query.limit === 'number' && Number.isInteger(query.limit)) {
    params.append('limit', String(query.limit));
  }
  if (typeof query.cursor === 'string' && query.cursor.trim() !== '') {
    params.append('cursor', query.cursor.trim());
  }
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ: `resolution` ที่
 * ผิดชนิดแล้วถูกกลืนเป็น `null` จะทำให้ conflict ที่ปิดไปแล้วกลับมาแสดงเป็นงานค้าง
 * พร้อมปุ่มที่กดแล้วได้ `409` เสมอ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  return raw as Record<string, unknown>;
}

/**
 * หนึ่งแถวของคิว — **บังคับครบทั้งแปดฟิลด์** ไม่ผ่อนให้ตัวใดหายไปได้
 *
 * ฟิลด์การปิดทั้งสี่ต้อง**มีอยู่** แม้จะเป็น `null` เพราะมันคือสิ่งที่บอกว่าแถวนี้
 * เปิดหรือปิด (`offline_sync_conflict` ไม่มีคอลัมน์ status — D-074) แถวที่ขาดฟิลด์
 * เหล่านี้ไปคือคำตอบที่เราอ่านไม่ออก ไม่ใช่ conflict ที่ยังเปิดอยู่
 */
export function parseOfflineSyncConflict(raw: unknown): OfflineSyncConflict | null {
  const record = asRecord(raw);
  if (record === null) {
    return null;
  }

  const conflictId = requiredString(record.conflict_id);
  const clientEventId = requiredString(record.client_event_id);
  const commandType = requiredString(record.command_type);
  const reason = requiredString(record.reason);
  const createdAt = requiredString(record.created_at);

  const resolution = nullableString(record.resolution);
  const resolvedBy = nullableString(record.resolved_by_employee_id);
  const resolvedAt = nullableString(record.resolved_at);

  if (
    conflictId === null ||
    clientEventId === null ||
    commandType === null ||
    reason === null ||
    createdAt === null ||
    resolution === undefined ||
    resolvedBy === undefined ||
    resolvedAt === undefined
  ) {
    return null;
  }

  return {
    conflict_id: conflictId,
    client_event_id: clientEventId,
    command_type: commandType,
    reason,
    resolution,
    resolved_by_employee_id: resolvedBy,
    resolved_at: resolvedAt,
    created_at: createdAt,
  };
}

/**
 * อ่าน `{ items, next_cursor }` — **และจากรูปนี้เท่านั้น**
 *
 * `items` ที่เป็น array ว่างผ่านได้ (เป็นคำตอบปกติของวันที่ไม่มีอะไรผิดพลาด) แต่
 * payload ที่ **ไม่มีคีย์ `items`** ไม่ผ่าน: สองอย่างนี้ต่างกันตรงที่อย่างหลังแปลว่า
 * เราอ่านคำตอบผิดรูป แล้วแสดงมันเป็น "ไม่มีอะไรค้าง" ซึ่งบนคิวนี้คือการซ่อนงานที่
 * รอคนตัดสินอยู่
 */
export function parseOfflineSyncConflictPage(raw: unknown): OfflineSyncConflictPage | null {
  const record = asRecord(raw);
  if (record === null || !Array.isArray(record.items)) {
    return null;
  }

  const items: OfflineSyncConflict[] = [];
  for (const entry of record.items) {
    const parsed = parseOfflineSyncConflict(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  // cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
  // จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  const record = asRecord(raw);
  if (record === null) {
    return EMPTY_ENVELOPE;
  }
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | OfflineConflictFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: OfflineConflictRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/offline-sync/conflicts` — คิวที่ยังไม่มีใครตัดสิน (หรือประวัติที่ปิดแล้ว)
 *
 * เก่าสุดอยู่บนตามที่เซิร์ฟเวอร์เรียงมา — client ไม่เรียงใหม่: การเรียงของคิวนี้คือ
 * "อันที่รอมานานที่สุดต้องถูกเห็นก่อน" ซึ่งเป็นการตัดสินใจฝั่งเซิร์ฟเวอร์ ไม่ใช่
 * รสนิยมของหน้าจอ
 */
export async function fetchOfflineSyncConflicts(
  query: OfflineSyncConflictQuery = {},
  options: OfflineConflictRequestOptions = {},
): Promise<OfflineConflictListResult> {
  const result = await requestPayload(
    `${OFFLINE_SYNC_CONFLICTS_PATH}${buildConflictListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseOfflineSyncConflictPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * body ของ `POST .../resolve` — **คีย์เดียว และการที่ไม่มีคีย์อื่นคือ contract**
 *
 * ไม่มี `resolved_by` (ผู้ตัดสินคือผู้ที่เข้าสู่ระบบ ไม่ใช่ค่าที่ request บอกได้ —
 * ไม่งั้นคนหนึ่งบันทึกการตัดสินในชื่อของอีกคนได้) ไม่มีโน้ต (Phase 1 ไม่มีคอลัมน์
 * ให้เก็บ — D-074/D-077 บริบทอยู่ใน `audit_log`) และไม่มีอะไรที่สั่งให้ post
 */
export function serializeResolveConflictBody(
  outcome: ConflictOutcome,
): Record<string, unknown> {
  return { outcome };
}

/**
 * `POST /v1/offline-sync/conflicts/{conflict_id}/resolve` — บันทึกการตัดสินของคน
 *
 * ⚠️ **ไม่ใช่ retry** คำสั่งเดิมไม่ถูก apply ใหม่ ไม่มีสต๊อกเคลื่อน ไม่มีเอกสาร
 * บัญชีเกิดขึ้น (business rule 5, D-077) สิ่งที่เกิดคือ `resolution`,
 * `resolved_by_employee_id`, `resolved_at` ถูกเขียนพร้อม `audit_log` ในธุรกรรมเดียว
 *
 * คำตอบคือ conflict ที่ปิดแล้ว — ผู้เรียกต้องอ่านสถานะใหม่จาก **คำตอบ** หรือจากการ
 * โหลดรายการใหม่ ไม่ใช่จากสิ่งที่ตัวเองเพิ่งส่งไป
 */
export async function resolveOfflineSyncConflict(
  conflictId: string,
  outcome: ConflictOutcome,
  options: OfflineConflictRequestOptions = {},
): Promise<OfflineConflictResolveResult> {
  const result = await requestPayload(
    conflictResolvePath(conflictId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeResolveConflictBody(outcome)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const conflict = parseOfflineSyncConflict(result.payload);
  return conflict === null ? { status: 'invalid-response' } : { status: 'ready', conflict };
}
